import { Globe, Plus, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Controller } from 'react-hook-form'

import Button from '@/elements/Button'
import ExternalLink from '@/elements/ExternalLink'
import Flex from '@/elements/Flex'
import Form from '@/elements/Form'
import FormInput from '@/elements/FormInput'
import FormSelect from '@/elements/FormSelect'
import Loading from '@/elements/Loading'
import Text from '@/elements/Text'
import Modal from '@/components/Modal'
import type { IndividualSocialPlatformGroupedResponse } from '@/api/types/Organization'
import { createIndividualSocialPlatform, deleteIndividualSocialPlatform } from '@/api/organizationIndividual'
import useBreakpoints from '@/hooks/useBreakpoints'
import useSocialChannelForm from '@/hooks/forms/useSocialChannelForm'
import toast from '@/utils/toast'
import { colors } from '@/utils/theme'
import { addSocialChannelEntry, availableSocialChannels, getFormattedSocialChannels } from '@/utils/socialChannels'

interface IndividualSocialChannelsProps {
  isTrustedThirdParty?: boolean
  individualId: string
  socialChannels: IndividualSocialPlatformGroupedResponse | null
  refetchSocialChannels: () => void
  isLoading?: boolean
}

function IndividualSocialChannels({
  isTrustedThirdParty = false,
  individualId,
  socialChannels,
  refetchSocialChannels,
  isLoading,
}: IndividualSocialChannelsProps) {
  const [showAddSocialChannelModal, setShowAddSocialChannelModal] = useState(false)
  const [socialChannelToRemoveId, setSocialChannelToRemoveId] = useState<string | null>(null)
  const { isMobile } = useBreakpoints()
  const { form: socialChannelForm, resetForm } = useSocialChannelForm()

  const platformsWithEntries = socialChannels ? getFormattedSocialChannels(socialChannels) : []

  const selectedType = socialChannelForm.watch('type')

  useEffect(() => {
    if (['website', 'other'].includes(selectedType)) {
      socialChannelForm.setValue('handle', '')
    }
  }, [selectedType, socialChannelForm])

  const handleChannelLinks = useCallback(
    async (action: 'add' | 'remove', platformId?: string) => {
      if (action === 'add') {
        const isValid = await socialChannelForm.trigger()
        if (!isValid) return

        const newItem = socialChannelForm.getValues()
        let valueToAdd = newItem.sourceUrl?.trim() ?? ''

        if (!valueToAdd && newItem.handle) {
          const channel = availableSocialChannels.find(c => c.value === newItem.type)
          if (channel?.baseUrl) {
            valueToAdd = `${channel.baseUrl}${newItem.handle.trim()}`
          }
        }

        if (!valueToAdd) return
        const body = addSocialChannelEntry(newItem.type, valueToAdd)
        try {
          await createIndividualSocialPlatform(individualId, body, isTrustedThirdParty)
          toast.success('Social channel added successfully!')
          setShowAddSocialChannelModal(false)
          refetchSocialChannels()
          resetForm()
        } catch {
          toast.error('Failed to add social channel')
        }
      } else {
        if (!platformId) return
        try {
          await deleteIndividualSocialPlatform(individualId, platformId)
          toast.success('Social channel removed successfully!')
          setSocialChannelToRemoveId(null)
          refetchSocialChannels()
        } catch {
          toast.error('Failed to remove social channel')
        }
      }
    }, [individualId, isTrustedThirdParty, refetchSocialChannels, resetForm, socialChannelForm])

  return (
    <Flex wfull align='flex-end' gap={6}>
      <Button
        variant='primary'
        fullWidth={isMobile}
        onClick={() => {
          setShowAddSocialChannelModal(true)
          resetForm()
        }}
      >
        <Plus size={18} />
        {isTrustedThirdParty ? 'Add Source' : 'Add Social Link'}
      </Button>
      {isLoading ? (
        <Flex wfull hfull inline justify='center' py={4}>
          <Loading />
        </Flex>
      ) : (platformsWithEntries.map(platform => {
        const Icon = platform.icon || Globe
        return (
          <Flex key={platform.key} wfull gap={4}>
            <Flex wfull inline align='center' gap={6}>
              <Text variant='h4'>{platform.name}:</Text>
              <Flex color='gray-light-20' grow style={{ height: '1px' }} />
            </Flex>
            {platform.channels.map((item, index) => (
              <Flex key={index} wfull inline align='center' justify='space-between' gap={2}>
                <Flex inline align='center' gap={2}>
                  <Flex
                    align='center'
                    justify='center'
                    radius='full'
                    style={{
                      minWidth: 43, maxWidth: 43,
                      minHeight: 43, maxHeight: 43,
                      border: `1px solid ${colors['gray-light-20']}`,
                      color: colors['black'],
                      overflow: 'hidden',
                    }}
                  >
                    {item.avatarUrl ? (
                      <img
                        src={item.avatarUrl}
                        alt={item.handle || platform.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Icon size={18} />
                    )}
                  </Flex>
                  <ExternalLink
                    to={item.url}
                    variant='black'
                    hasIcon={false}
                    maxLines={2}
                    disabled={!item.url.startsWith('http')}
                    wrapped
                  >
                    {item.handle || item.url}
                  </ExternalLink>
                </Flex>
                <Button
                  small
                  variant='iconDangerBtn'
                  onClick={() => setSocialChannelToRemoveId(item.id)}
                >
                  <X size={18} />
                </Button>
              </Flex>
            ))}
          </Flex>
        )
      }))}
      <Modal
        isOpen={!!socialChannelToRemoveId}
        title='Remove Social Channel'
        onSave={() => handleChannelLinks('remove', socialChannelToRemoveId ?? undefined)}
        onCancel={() => setSocialChannelToRemoveId(null)}
        cancelLabel='No'
        saveLabel='Yes'
      >
        <Text color='black' style={{ hyphens: 'none' }}>
          Are you sure you want to remove this social channel? This action cannot be undone.
        </Text>
      </Modal>
      <Modal
        title={isTrustedThirdParty ? 'Add Source' : 'Add Social Link'}
        isOpen={showAddSocialChannelModal}
        onSave={() => handleChannelLinks('add')}
        onCancel={() => {
          setShowAddSocialChannelModal(false)
          resetForm()
        }}
        disabled={!socialChannelForm.formState.isDirty}
        style={{ width: 538 }}
        preventClickOutsideClose={socialChannelForm.formState.isDirty}
      >
        <Form>
          <Flex className='form-group'>
            <Controller
              control={socialChannelForm.control}
              name='type'
              render={({ field }) => (
                <FormSelect
                  {...field}
                  label='Platform'
                  placeholder='Select platform'
                  Icon={field.value ? availableSocialChannels.find(c => c.value === field.value)?.icon : undefined}
                  values={field.value ? [availableSocialChannels.find(c => c.value === field.value)!] : []}
                  onChange={items => field.onChange(items.length > 0 ? items[0].value : null)}
                  options={availableSocialChannels}
                  optionLabel={item => item.label}
                  optionValue={item => item.value}
                  // optionIcon={item => item.icon!}
                  error={socialChannelForm.formState.errors.type?.message}
                  dirty={!!socialChannelForm.formState.dirtyFields.type}
                />
              )}
            />
            {!['website', 'other'].includes(selectedType) && (
              <Controller
                control={socialChannelForm.control}
                name='handle'
                render={({ field }) => (
                  <FormInput
                    {...field}
                    label='Handle'
                    placeholder='e.g. username'
                    error={socialChannelForm.formState.errors.handle?.message}
                    dirty={!!socialChannelForm.formState.dirtyFields.handle}
                    onChange={e => {
                      let value = e.target.value
                      if (value.startsWith('@')) {
                        value = value.slice(1)
                      }
                      socialChannelForm.setValue('sourceUrl', '')
                      field.onChange(value)
                    }}
                  />
                )}
              />
            )}
          </Flex>
          <Controller
            control={socialChannelForm.control}
            name='sourceUrl'
            render={({ field }) => (
              <FormInput
                {...field}
                label='URL'
                type='url'
                placeholder='https://'
                error={socialChannelForm.formState.errors.sourceUrl?.message}
                dirty={!!socialChannelForm.formState.dirtyFields.sourceUrl}
                disabled={!!socialChannelForm.getValues('handle')}
              />
            )}
          />
        </Form>
      </Modal>
    </Flex>
  )
}

export default IndividualSocialChannels
